import React, { forwardRef } from 'react';
import styled from 'styled-components';
import {
    blue60,
    blue100,
    slate20,
    slate30,
    slate40,
    slate70,
    negativeMedium,
} from '@zocdoc-mezzanine/foundations-colors';
import {
    paragraphSmall,
    labelSmall,
} from '@zocdoc-mezzanine/foundations-typography';
import BaseTextfield, {
    InputWrapper,
    LeftIconWrapper,
    RightIconWrapper,
} from './private/BaseTextfield';

/**
 * Textfield
 * @param {TextfieldProps} props - Props for the textfield.
 * @returns {JSX.Element} Textfield
 */
const Textfield = forwardRef((props, ref) => {
    const {
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledby,
        'aria-describedby': ariaDescribedby,
        placeholder,
        autocomplete,
        inputmode,
        pattern,
        id,
        name,
        value,
        labelText,
        disabled,
        maxlength,
        invalidMessage,
        required,
        onChange,
        onFocus,
        onBlur,
        type,
        leftIcon,
        rightIcon,
        'data-test': dataTest,
        'data-field-id': dataFieldId,
    } = props;

    return (
        <StyledTextarea
            labelText={labelText}
            aria-describedby={ariaDescribedby}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            data-field-id={dataFieldId}
            data-test={dataTest}
            ref={ref}
            id={id}
            pattern={pattern}
            autocomplete={autocomplete}
            inputmode={inputmode}
            placeholder={placeholder}
            value={value}
            invalidMessage={invalidMessage}
            disabled={disabled}
            maxlength={maxlength}
            name={name}
            leftIcon={leftIcon}
            rightIcon={rightIcon}
            required={required}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            type={type}
        />
    );
});

const StyledTextarea = styled(BaseTextfield)`
    label {
        ${labelSmall}
        color: ${blue100};
        margin-bottom: 4px;
        display: inline-block;
    }

    ${InputWrapper} {
        ${paragraphSmall}
        height: 44px;
        border: 1px solid ${slate30};

        ${(props) =>
            props.disabled &&
            `
              background-color: ${slate20};
        `}

        &[data-input-has-focus=true] {
            outline: 2px solid ${blue60};
            border: none;
        }

        &[data-invalid='true'] {
            border-color: ${negativeMedium};

            &[data-input-has-focus='true'] {
                outline: 2px solid ${negativeMedium};
            }
        }
    }

    ${LeftIconWrapper} {
        padding: 12px 0 12px 16px;
    }

    ${RightIconWrapper} {
        padding: 12px 16px 12px 0;
    }

    input {
        ${paragraphSmall}
        color: ${blue100};
        padding: 12px 22px 12px 12px;

        // Most modern browswers support ::placeholder
        ::placeholder,
        ::-webkit-input-placeholder {
            color: ${slate70};

            ${(props) =>
                props.disabled &&
                `
                  color: ${slate40};
            `}
        }

        // Removes arrows on number input.
        &[type='number']::-webkit-inner-spin-button,
        &[type='number']::-webkit-outer-spin-button {
            appearance: none;
            margin: 0;
        }
        &[type='number'] {
            -moz-appearance: textfield;
        }

        ${(props) =>
            props.disabled &&
            `
              // background needs to be set here and above because FF does not support the inheritance for some reason.
              background-color: ${slate20};
              color: ${slate40};
        `}
    }
`;

export default Textfield;
